import React, {useContext, useState} from 'react';
import { Box, Select, Button, Text, Tooltip, Flex, useToast } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Combine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import fetchData from "./Util";
import {ConfigContext} from "./Config";

const GroupEditor = ({ bid, item, onGroupUpdate, itemsBidData }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(bid?.bids_group_name || '');
  const toast = useToast();
  const config = useContext(ConfigContext);

  const groups = ['none', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];

  const hasNonPausedItemsInGroup = (groupName) => {
    if (!groupName) return false;
    
    const groupBids = itemsBidData?.flatMap(sale => 
      sale.data.flatMap(item => [
        ...(item.bid || []),
        ...(item.book_bid || []),
        ...(item.agent_bid || [])
      ])
    ).filter(b => b.bids_group_name === groupName);

    return groupBids?.some(groupBid => {
      const bidItem = itemsBidData?.flatMap(sale => sale.data)
        .find(item => item.uuid === groupBid.item_uuid);
      return bidItem?.status !== 'paused';
    });
  };

  const handleSave = async () => {
    try {
      const url = `${config.backendUrl}/api/bids/${bid.id}?access_token=${config.userData.bo_access_token}`;
      const bidData = { bids_group_name: selectedGroup === 'none' ? null : selectedGroup }
      const response = await fetchData(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bidData),
      });

      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to update group');
      }
  
      toast({
        title: t('myBids.groupUpdateSuccess'),
        description: selectedGroup === 'none' 
          ? t('myBids.groupRemoved')
          : t('myBids.groupAssigned', { group: selectedGroup.toUpperCase() }),
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
  
      setIsEditing(false);
      // Update the parent component's state instead of reloading
      onGroupUpdate(bid.id, selectedGroup === 'none' ? null : selectedGroup);
    } catch (error) {
      console.error('Error details:', error);
      toast({
        title: t('myBids.error'),
        description: error.message || t('myBids.groupUpdateError'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const groupDisplay = (
    <Flex alignItems="center" gap={1}>
      <Box
        fontSize="sm"
        fontWeight="bold"
        color="gray.700"
      >
        {t('myBids.groupName', { group: bid?.bids_group_name?.toUpperCase() })}
      </Box>
    </Flex>
  );

  const orBidsExplanation = (
    <Box>
      <Text fontWeight="bold" mb={2}>{t('myBids.orGroupExplanation.title')}</Text>
      <Text mb={2}>{t('myBids.orGroupExplanation.main')}</Text>
      <Text>{t('myBids.orGroupExplanation.example')}</Text>
    </Box>
  );

  // Check if editing is allowed based on item status and bid state
  const canEdit = item?.status === 'paused' && !bid?.deleted && bid?.author === config.userData.id;


  if (!bid) return null;

  return (
    <Box>
      {!bid.bids_group_name && !isEditing ? (
        // Only show Add to Group button if editing is allowed
        canEdit ? (
          <Box fontSize="sm" textAlign="center">
            <Tooltip
              label={orBidsExplanation}
              hasArrow
              placement="top"
              openDelay={300}
              maxWidth="400px"
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(true)}
                leftIcon={<Combine size={16} />}
              >
                {t('myBids.addToGroup')}
              </Button>
            </Tooltip>
          </Box>
        ) : null
      ) : isEditing ? (
        <Box>
          <Select
            size="sm"
            value={selectedGroup}
            onChange={(e) => {
              const newGroup = e.target.value;
              if (newGroup !== 'none' && hasNonPausedItemsInGroup(newGroup)) {
                toast({
                  title: t('myBids.error'),
                  description: t('myBids.groupHasClosedItems'),
                  status: 'error',
                  duration: 9000,
                  isClosable: true,
                });
                return;
              }
              setSelectedGroup(newGroup);
            }}
            width="100%"
            mb={2}
          >
            {groups.map(group => (
              <option key={group} value={group}>
                {group === 'none' ? t('myBids.noGroup') : t('myBids.groupName', { group: group.toUpperCase() })}
              </option>
            ))}
          </Select>
          <Flex justifyContent="space-between" width="100%">
            <Button size="sm" onClick={handleSave}>{t('myBids.save')}</Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsEditing(false)}
            >
              <CloseIcon w={3} h={3} />
            </Button>
          </Flex>
        </Box>
      ) : (
        <Flex gap={2} alignItems="center" justifyContent="center">
          {bid.alternative_bids?.length > 0 ? (
            <Tooltip
              label={t('myBids.alternativeLots', {
                lots: bid.alternative_bids.map(alt => alt.lot).join(', ')
              })}
              hasArrow
            >
              {groupDisplay}
            </Tooltip>
          ) : groupDisplay}
          {/* Only show Edit button if editing is allowed */}
          {canEdit && (
            <Tooltip
              label={orBidsExplanation}
              hasArrow
              placement="top"
              openDelay={300}
              maxWidth="400px"
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(true)}
                leftIcon={<Combine size={14} />}
              >
                {t('myBids.edit')}
              </Button>
            </Tooltip>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default GroupEditor;